/** @flow */
import React, { useRef, useState } from "react";
import { localize } from "redux-i18n";
import { Button, Loader, Text, View } from "@components";
import { SchemaForm } from "@components";
import type { AITeamSettings } from "@modules/client/reducer/service/types";
import uiSchema from "./ui-schema";

type Props = $Exact<{
  ...PropsFromLocalize,
  settings: AITeamSettings | null,
  submitForm: AITeamSettings => void,
  disableEdit?: boolean,
}>;

export function AIFeatures(props: Props) {
  const [edit, setEdit] = useState(false);
  const formRef = useRef(null);

  const submitSchemaForm = () => {
    formRef.current && formRef.current.submit();
  };

  const submitHandler = (formData: { ...AITeamSettings, aiTeamEnabled: boolean }) => {
    let res;

    // delete formData.aiTeamEnabled;
    if (!!formData?.aiTeamEnabled) {
      res = {
        chatbotBomanEnabled: formData.chatbotBomanEnabled,
        chatbotClaudiaEnabled: formData.chatbotClaudiaEnabled,
        chatbotMorganEnabled: formData.chatbotMorganEnabled,
        chatbotShaniEnabled: formData.chatbotShaniEnabled,
      };
    } else {
      res = {
        chatbotBomanEnabled: false,
        chatbotClaudiaEnabled: false,
        chatbotMorganEnabled: false,
        chatbotShaniEnabled: false,
      };
    }

    props.submitForm(res);
    setEdit(false);
  };

  const cancelEdit = () => {
    setEdit(false);
  };

  if (!props.settings) {
    return <Loader />;
  }

  const settings: AITeamSettings = props.settings;
  const data: AITeamSettings = {
    chatbotBomanEnabled:
      settings.chatbotBomanEnabled !== undefined ? settings.chatbotBomanEnabled : true,
    chatbotClaudiaEnabled:
      settings.chatbotClaudiaEnabled !== undefined ? settings.chatbotClaudiaEnabled : false,
    chatbotMorganEnabled:
      settings.chatbotMorganEnabled !== undefined ? settings.chatbotMorganEnabled : false,
    chatbotShaniEnabled:
      settings.chatbotShaniEnabled !== undefined ? settings.chatbotShaniEnabled : false,
  };

  // aiTeamEnabled is now part of the BE payload, so we need to add it here to make the schema form work properly
  // it will be removed after submitting the form
  const _data = {
    aiTeamEnabled:
      data.chatbotBomanEnabled ||
      data.chatbotClaudiaEnabled ||
      data.chatbotMorganEnabled ||
      data.chatbotShaniEnabled,
    ...data,
  };

  return (
    <View gap={6}>
      <View direction="row" justifyContent="space-between">
        <Text type="h6">{props.t("AI_FEATURES")}</Text>
        {!props.disableEdit && (
          <View direction="row" justifyContent="space-between" gap="4">
            {!edit ? (
              <Button materialIcon="edit" onClick={() => setEdit(true)} size="small">
                {props.t("EDIT")}
              </Button>
            ) : (
              <View direction="row" gap="4">
                <Button color="secondary" onClick={cancelEdit} size="small">
                  {props.t("CANCEL")}
                </Button>
                <Button
                  materialIcon="replay_circle_filled"
                  className="ml-2"
                  onClick={submitSchemaForm}
                  size="small"
                >
                  {props.t("SUBMIT")}
                </Button>
              </View>
            )}
          </View>
        )}
      </View>
      <SchemaForm
        externalSubmit
        schema={schema}
        uiSchema={uiSchema(props.t)}
        formData={_data}
        ref={formRef}
        onSubmit={submitHandler}
        readonly={!edit}
        boxGrid
      />
    </View>
  );
}

export default localize()(AIFeatures);

const schema = {
  type: "object",
  properties: {
    aiTeamEnabled: {
      type: "boolean",
      title: "AI Team",
    },
  },
  if: {
    properties: {
      aiTeamEnabled: { const: true },
    },
  },
  then: {
    properties: {
      aiTeamEnabled: {
        type: "boolean",
        title: "AI Team",
      },
      chatbotBomanEnabled: {
        type: "boolean",
        title: "Boman",
      },
      chatbotClaudiaEnabled: {
        type: "boolean",
        title: "Claudia",
      },
      chatbotMorganEnabled: {
        type: "boolean",
        title: "Morgan",
      },
      chatbotShaniEnabled: {
        type: "boolean",
        title: "Shani",
      },
    },
  },
  else: {
    properties: {
      aiTeamEnabled: {
        type: "boolean",
        title: "AI Team",
      },
    },
  },
};
